import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  Plus, Play, Edit, Trash2, Calendar, Eye, Loader2,
  CheckCircle, XCircle, AlertTriangle, ShieldCheck,
  Gamepad2, ChevronLeft, ChevronRight, ExternalLink,
} from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { GameStatus } from '@/lib/game-enums';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GameDetailModal } from '@/components/games/GameDetailModal';
import { NewGameModal } from '@/components/games/NewGameModal';
import { useAuthStore } from '@/stores/auth';
import type { AdminGame } from '@/components/games/types';

const PAGE_SIZE = 20;

interface PoolValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  pool_count: number;
  expected_count: number;
  pool_hash: string | null;
}

interface LotepPoolInfo {
  id: string;
  status: string;
  commitment_hash: string;
  numbers_total: number;
  numbers_consumed: number;
  created_at: string;
}

interface LotepStructure {
  id: string;
  lotep_code: string;
  modality_slug: string;
  modality_slugs?: string[];
  authorized_at: string;
  expires_at?: string;
  max_pool_value_brl: string;
  max_ticket_value_brl: string;
  total_numbers: number;
  status: string;
  pools?: LotepPoolInfo[];
}

const MODALITY_LABELS: Record<string, string> = {
  girodeouro: 'Giro de Ouro',
  cacapix: 'CaçaPix',
  raspouepix: 'Raspou e Pix',
  baudotesouro: 'Baú do Tesouro',
};

export const Route = createFileRoute('/_admin/jogos/cadastrados')({
  component: JogosCadastradosPage,
});

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function activePool(pools?: LotepPoolInfo[]): LotepPoolInfo | null {
  if (!pools?.length) return null;
  const open = pools.find(p => p.status === 'open' || p.status === 'active');
  return open ?? pools[0];
}

function LotepStructureRow({
  structure,
  draftCount,
  publishing,
  onPublish,
}: {
  structure: LotepStructure;
  draftCount: number;
  publishing: boolean;
  onPublish: () => void;
}) {
  const pool = activePool(structure.pools);
  const slugs = structure.modality_slugs?.length
    ? structure.modality_slugs
    : [structure.modality_slug];

  const consumed = pool?.numbers_consumed ?? 0;
  const total = pool?.numbers_total ?? structure.total_numbers ?? 0;
  const pct = total > 0 ? Math.round((consumed / total) * 100) : 0;

  return (
    <TableRow>
      <TableCell>
        <div>
          <p className="font-medium font-mono text-sm">{structure.lotep_code}</p>
          <p className="text-xs text-muted-foreground">
            Max R$ {Number(structure.max_pool_value_brl).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            {structure.expires_at && (
              <> · expira {format(new Date(structure.expires_at), 'dd/MM/yyyy', { locale: ptBR })}</>
            )}
          </p>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {slugs.map(slug => (
            <Badge key={slug} variant="secondary" className="text-xs">
              {MODALITY_LABELS[slug] ?? slug}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant={structure.status === 'active' ? 'default' : 'outline'}
          className={structure.status === 'active' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          {structure.status}
        </Badge>
      </TableCell>
      <TableCell>
        {pool ? (
          <div className="space-y-1 min-w-[120px]">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{consumed.toLocaleString('pt-BR')}</span>
              <span>{total.toLocaleString('pt-BR')}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-right">{pct}%</p>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Sem pool</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {draftCount > 0 && (
            <Button
              size="sm"
              onClick={onPublish}
              disabled={publishing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {publishing ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Play className="h-3 w-3 mr-1" />
              )}
              Publicar ({draftCount})
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/lotep/estruturas/$structureId/pool" params={{ structureId: structure.id }}>
              <ExternalLink className="h-3 w-3 mr-1" />
              Ver Pool
            </Link>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function JogosCadastradosPage() {
  const queryClient = useQueryClient();
  const session = useAuthStore(s => s.session);
  const isReadOnly = session?.role === 'moderator';

  const [isNewGameOpen, setIsNewGameOpen] = useState(false);
  const [editingGame, setEditingGame] = useState<AdminGame | null>(null);
  const [gameToDelete, setGameToDelete] = useState<AdminGame | null>(null);
  const [gameToView, setGameToView] = useState<AdminGame | null>(null);
  const [validationResults, setValidationResults] = useState<Record<string, PoolValidation>>({});
  const [publishingGameId, setPublishingGameId] = useState<string | null>(null);
  const [publishingStructureId, setPublishingStructureId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [publishDebounce, setPublishDebounce] = useState<Record<string, boolean>>({});

  const { data: games, isLoading: gamesLoading } = useQuery<AdminGame[]>({
    queryKey: ['admin-games-registered'],
    queryFn: async () => {
      const response = await api.get('/admin/games', { params: { status: 'draft,scheduled', limit: 500 } });
      const list = (response.data?.data ?? response.data ?? []) as AdminGame[];
      return list.map(game => ({
        ...game,
        modality: (game as AdminGame & { modality_info?: AdminGame['modality'] }).modality_info ?? game.modality ?? null,
      }));
    },
  });

  const { data: lotepStructures, isLoading: structuresLoading } = useQuery<LotepStructure[]>({
    queryKey: ['admin-lotep-structures-cadastrados'],
    queryFn: async () => {
      const r = await api.get('/admin/lotep/structures');
      return (r.data?.structures ?? []) as LotepStructure[];
    },
  });

  const generatePoolMutation = useMutation({
    mutationFn: async (gameId: string) => {
      const data = await api.post<{ success: boolean; error?: string }>(`/admin/games/${gameId}/generate-pool`);
      if (!data.data.success) throw new Error(data.data.error || 'Falha na geração da pool');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      toast.success('Pool gerada com sucesso!');
    },
    onError: (error: unknown) => {
      const apiMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const raw = apiMsg || (error instanceof Error ? error.message : '');
      const msg = /does not match|SUM\(game_prizes/i.test(raw)
        ? 'A soma das quantidades de prêmios precisa ser exatamente igual ao total de números do jogo. Aloque um prêmio "Sem Prêmio" para cobrir os números restantes.'
        : (raw || 'Erro ao gerar pool');
      toast.error(msg);
    },
  });

  const validatePoolMutation = useMutation({
    mutationFn: async (gameId: string) => {
      const r = await api.post<PoolValidation>(`/admin/games/${gameId}/validate-pool`);
      return { data: r.data, gameId };
    },
    onSuccess: ({ data, gameId }) => {
      setValidationResults(prev => ({ ...prev, [gameId]: data }));
      if (data.valid) {
        toast.success('Pool validada com sucesso!');
      } else {
        toast.error(`Pool inválida: ${data.errors.join(', ')}`);
      }
      if (data.warnings?.length > 0) {
        toast.warning(`Avisos: ${data.warnings.join(', ')}`);
      }
    },
    onError: (error: unknown) => {
      const apiMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const msg = apiMsg || (error instanceof Error ? error.message : 'Erro ao validar pool');
      toast.error(msg);
    },
  });

  const handlePublishGame = async (gameId: string) => {
    if (publishDebounce[gameId] || publishingGameId === gameId) return;

    setPublishDebounce(prev => ({ ...prev, [gameId]: true }));
    setPublishingGameId(gameId);
    setTimeout(() => setPublishDebounce(prev => ({ ...prev, [gameId]: false })), 2000);

    try {
      const currentGame = games?.find(g => g.id === gameId);

      if (!currentGame?.pool_generated) {
        toast.info('Gerando pool automaticamente...');
        const poolData = await api.post<{ success: boolean; error?: string }>(`/admin/games/${gameId}/generate-pool`);
        if (!poolData.data.success) throw new Error(poolData.data.error || 'Falha na geração');
        toast.success('Pool gerada com sucesso!');
      }

      const validation = await api.post<PoolValidation>(`/admin/games/${gameId}/validate-pool`);
      setValidationResults(prev => ({ ...prev, [gameId]: validation.data }));

      if (!validation.data.valid) throw new Error(validation.data.errors.join('; '));

      await api.patch(`/admin/games/${gameId}`, {
        status: 'active' as GameStatus,
        start_at: new Date().toISOString(),
      });

      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-active'] });
      toast.success('Jogo publicado com sucesso!');
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, 'Erro ao publicar jogo'));
    } finally {
      setPublishingGameId(null);
    }
  };

  // Publica todos os jogos rascunho de uma estrutura LOTEP (pool compartilhada).
  const handlePublishStructure = async (structureId: string, gameIds: string[]) => {
    if (publishingStructureId || gameIds.length === 0) return;
    setPublishingStructureId(structureId);
    let ok = 0;
    try {
      for (const gameId of gameIds) {
        const currentGame = games?.find(g => g.id === gameId);
        if (!currentGame?.pool_generated) {
          const poolData = await api.post<{ success: boolean; error?: string }>(`/admin/games/${gameId}/generate-pool`);
          if (!poolData.data.success) throw new Error(poolData.data.error || 'Falha na geração da pool');
        }
        const validation = await api.post<PoolValidation>(`/admin/games/${gameId}/validate-pool`);
        if (!validation.data.valid) throw new Error(validation.data.errors.join('; '));
        await api.patch(`/admin/games/${gameId}`, {
          status: 'active' as GameStatus,
          start_at: new Date().toISOString(),
        });
        ok += 1;
      }
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-active'] });
      toast.success(`Estrutura publicada (${ok}/${gameIds.length} jogos).`);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, `Erro ao publicar estrutura (${ok}/${gameIds.length} publicados)`));
    } finally {
      setPublishingStructureId(null);
    }
  };

  const deleteGameMutation = useMutation({
    mutationFn: async (gameId: string) => {
      await api.delete(`/admin/games/${gameId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      toast.success('Jogo excluido com sucesso!');
      setGameToDelete(null);
    },
    onError: (error: unknown) => {
      const apiMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const msg = apiMsg || (error instanceof Error ? error.message : 'Erro ao excluir jogo');
      toast.error(msg);
    },
  });

  const allGames = games ?? [];
  // Jogos individuais = sem pool LOTEP
  const individualGames = allGames.filter(g => !g.use_lotep_pool);
  const totalPages = Math.ceil(individualGames.length / PAGE_SIZE);
  const paginatedGames = individualGames.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const structures = lotepStructures ?? [];
  // Contar jogos LOTEP (use_lotep_pool=true) para o card de sumário
  const lotepGamesCount = allGames.filter(g => g.use_lotep_pool).length;

  const isLoading = gamesLoading || structuresLoading;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Gamepad2}
        title="Jogos Cadastrados"
        description="Crie, edite e publique jogos individuais (rascunhos e agendados)"
        actions={
          !isReadOnly ? (
            <Button onClick={() => setIsNewGameOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Jogo
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Cadastrados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allGames.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Estruturas LOTEP</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{structures.length}</div>
            {lotepGamesCount > 0 && (
              <p className="text-xs text-muted-foreground">{lotepGamesCount} jogos vinculados</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Agendados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allGames.filter(g => g.start_at).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Prêmios Totais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(individualGames.reduce((sum, g) => sum + (g.total_plays * g.play_price || 0), 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seção LOTEP — agrupada por estrutura */}
      {(structures.length > 0 || structuresLoading) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-base">Estruturas LOTEP (Pool Compartilhada)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código LOTEP</TableHead>
                  <TableHead>Modalidades</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Jogadas (consumido/total)</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {structuresLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6">Carregando estruturas...</TableCell>
                  </TableRow>
                ) : structures.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      Nenhuma estrutura LOTEP encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  structures.map(s => {
                    const draftGameIds = allGames
                      .filter(g => g.use_lotep_pool && g.status === 'draft' && (g.title ?? '').includes(s.lotep_code))
                      .map(g => g.id);
                    return (
                      <LotepStructureRow
                        key={s.id}
                        structure={s}
                        draftCount={draftGameIds.length}
                        publishing={publishingStructureId === s.id}
                        onPublish={() => handlePublishStructure(s.id, draftGameIds)}
                      />
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Seção jogos individuais (sem LOTEP) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Jogos Individuais</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Jogo</TableHead>
                <TableHead>Modalidade</TableHead>
                <TableHead>Tema</TableHead>
                <TableHead>Preco</TableHead>
                <TableHead>Jogadas</TableHead>
                <TableHead>Inicio Agendado</TableHead>
                <TableHead>Pool</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Carregando...</TableCell>
                </TableRow>
              ) : individualGames.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Nenhum jogo individual cadastrado. Clique em "Novo Jogo" para comecar.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedGames.map(game => (
                  <TableRow key={game.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {(game.theme?.theme_assets?.avatar_image_url ?? game.icon_url) && (
                          <img src={game.theme?.theme_assets?.avatar_image_url ?? game.icon_url ?? ''} alt={`Ícone ${game.title}`} className="h-10 w-10 rounded-lg object-cover" loading="lazy" />
                        )}
                        <div>
                          <p className="font-medium">{game.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{game.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{game.modality?.icon} {game.modality?.name || '-'}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{game.theme?.name || '-'}</span>
                    </TableCell>
                    <TableCell>{formatCurrency(game.play_price)}</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {game.used_plays.toLocaleString('pt-BR')}/{game.total_plays.toLocaleString('pt-BR')}
                      </span>
                    </TableCell>
                    <TableCell>
                      {game.start_at ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(game.start_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Nao agendado</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <div className="flex items-center gap-1.5">
                          <Badge variant={game.pool_generated ? 'default' : 'secondary'}>
                            {game.pool_generated ? 'Gerado' : 'Pendente'}
                          </Badge>
                          {game.pool_generated && (
                            <>
                              {validationResults[game.id] ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="cursor-help">
                                      {validationResults[game.id].valid ? (
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-destructive" />
                                      )}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs">
                                    {validationResults[game.id].valid ? (
                                      <p className="text-green-500">Pool valida: {validationResults[game.id].pool_count} jogadas</p>
                                    ) : (
                                      <div className="text-destructive">
                                        <p className="font-semibold">Erros:</p>
                                        <ul className="text-xs list-disc pl-3">
                                          {validationResults[game.id].errors.map((err, i) => (
                                            <li key={i}>{err}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="cursor-help">
                                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>Validação pendente - clique em validar</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-2">
                        {!isReadOnly && (
                          <Button
                            size="sm"
                            onClick={() => handlePublishGame(game.id)}
                            disabled={publishingGameId === game.id || publishDebounce[game.id]}
                            className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                          >
                            {publishingGameId === game.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Play className="h-4 w-4 mr-1" />
                            )}
                            Publicar
                          </Button>
                        )}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setGameToView(game)} title="Ver detalhes" aria-label="Ver detalhes">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setEditingGame(game)} title="Editar" aria-label="Editar">
                            <Edit className="h-4 w-4" />
                          </Button>
                          {game.pool_generated && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => validatePoolMutation.mutate(game.id)}
                              disabled={validatePoolMutation.isPending}
                              title="Validar pool"
                              aria-label="Validar pool"
                              className="text-blue-500 hover:text-blue-600"
                            >
                              {validatePoolMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ShieldCheck className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {!isReadOnly && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setGameToDelete(game)}
                              className="text-destructive hover:text-destructive"
                              title="Excluir"
                              aria-label="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">
                Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, individualGames.length)} de {individualGames.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs px-2 text-muted-foreground">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <NewGameModal
        open={isNewGameOpen || !!editingGame}
        onOpenChange={open => {
          if (!open) { setIsNewGameOpen(false); setEditingGame(null); }
        }}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] })}
        game={editingGame}
      />

      {gameToView && (
        <GameDetailModal
          open={!!gameToView}
          onOpenChange={() => setGameToView(null)}
          game={gameToView}
        />
      )}

      <ConfirmModal
        isOpen={!!gameToDelete}
        onClose={() => setGameToDelete(null)}
        onConfirm={() => { if (gameToDelete) deleteGameMutation.mutate(gameToDelete.id); }}
        title="Confirmar Exclusão"
        description={`Tem certeza que deseja excluir o jogo "${gameToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
      />

      {generatePoolMutation.isPending && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-card border rounded-lg p-3 shadow-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Gerando pool...</span>
        </div>
      )}
    </div>
  );
}
